/**
 * Where the spotlight of a tour stop is and where its card goes. Pure, so the tests need no DOM.
 * All values are viewport pixels (the layer is `position: fixed`).
 */

/** The space between the target and the edge of the spotlight. */
export const TOUR_HOLE_PADDING = 8;
/** The space between the spotlight and the card. */
export const TOUR_CARD_GAP = 14;
/** The smallest space between the card and the edge of the screen. */
export const TOUR_EDGE = 16;
/** Below this width the card is a bottom sheet. Keep it equal to `$tour-narrow` in the stylesheet. */
export const TOUR_NARROW_WIDTH = 720;

export interface TourBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface TourSize {
  width: number;
  height: number;
}

/**
 * - `below` / `above`: under or over the spotlight, never on it.
 * - `right` / `left`: beside the spotlight, when neither under nor over it has room.
 * - `over`: no room on any side (the user scrolled a tall target): the card sits at the foot of the
 *   screen, over the spotlight. `tourScrollDelta` scrolls the target first, so a stop opens with
 *   room for the card.
 * - `center`: no target on the page: a centered modal card.
 * - `sheet`: a narrow screen: a bottom sheet.
 */
export type TourCardPlacement = 'below' | 'above' | 'right' | 'left' | 'over' | 'center' | 'sheet';

export interface TourCardPosition {
  placement: TourCardPlacement;
  /** Only for `below`, `above`, `right`, `left` and `over`; CSS places `center` and `sheet`. */
  top?: number;
  left?: number;
}

/** The spotlight around a target: the target box plus the padding. */
export function tourHoleOf(target: TourBox): TourBox {
  return {
    top: target.top - TOUR_HOLE_PADDING,
    left: target.left - TOUR_HOLE_PADDING,
    width: target.width + TOUR_HOLE_PADDING * 2,
    height: target.height + TOUR_HOLE_PADDING * 2,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

/** The place of the card for a spotlight (null: no target) and the measured card size. */
export function placeTourCard(hole: TourBox | null, card: TourSize, viewport: TourSize): TourCardPosition {
  if (viewport.width < TOUR_NARROW_WIDTH) {
    return { placement: 'sheet' };
  }
  if (hole === null) {
    return { placement: 'center' };
  }
  const left = clamp(hole.left, TOUR_EDGE, Math.max(TOUR_EDGE, viewport.width - card.width - TOUR_EDGE));
  const holeBottom = hole.top + hole.height;
  const roomBelow = viewport.height - holeBottom - TOUR_CARD_GAP - TOUR_EDGE;
  if (roomBelow >= card.height) {
    return { placement: 'below', top: Math.max(TOUR_EDGE, holeBottom + TOUR_CARD_GAP), left };
  }
  const roomAbove = hole.top - TOUR_CARD_GAP - TOUR_EDGE;
  if (roomAbove >= card.height) {
    return { placement: 'above', top: hole.top - TOUR_CARD_GAP - card.height, left };
  }
  // Beside the spotlight: the top follows the spotlight, inside the screen.
  const sideTop = clamp(hole.top, TOUR_EDGE, Math.max(TOUR_EDGE, viewport.height - card.height - TOUR_EDGE));
  const fitsHeight = card.height + TOUR_EDGE * 2 <= viewport.height;
  const holeRight = hole.left + hole.width;
  if (fitsHeight && viewport.width - holeRight - TOUR_CARD_GAP - TOUR_EDGE >= card.width) {
    return { placement: 'right', top: sideTop, left: holeRight + TOUR_CARD_GAP };
  }
  if (fitsHeight && hole.left - TOUR_CARD_GAP - TOUR_EDGE >= card.width) {
    return { placement: 'left', top: sideTop, left: hole.left - TOUR_CARD_GAP - card.width };
  }
  return { placement: 'over', top: Math.max(TOUR_EDGE, viewport.height - card.height - TOUR_EDGE), left };
}

/**
 * How `scrollIntoView` puts a target on screen: the middle when the card still fits under it,
 * else the top. A narrow screen always takes the top, because the bottom sheet covers the foot.
 */
export function tourScrollBlock(targetHeight: number, viewport: TourSize, cardHeight: number): ScrollLogicalPosition {
  if (viewport.width < TOUR_NARROW_WIDTH) {
    return 'start';
  }
  const roomForCard = cardHeight + TOUR_CARD_GAP + TOUR_EDGE + TOUR_HOLE_PADDING * 2;
  return targetHeight + roomForCard * 2 <= viewport.height ? 'center' : 'start';
}

/** True when the spotlight is fully on screen. */
function holeIsVisible(hole: TourBox, viewport: TourSize): boolean {
  return hole.top >= 0 && hole.top + hole.height <= viewport.height;
}

/**
 * How far the scroller of the target moves (pixels, down is positive) when a stop opens, so the
 * card fits next to the spotlight and never covers it (onboarding review 2026-10-19, finding 4).
 * - 0 when the spotlight is on screen and the card has room on a side.
 * - A target that fits on the screen with the card under it: the pair goes to the middle, card below.
 * - A taller target: its top goes just under the card, so the card sits above it and the start of
 *   the target shows under the card.
 * A narrow screen has its bottom sheet and returns null: the caller keeps `scrollIntoView`.
 */
export function tourScrollDelta(hole: TourBox, card: TourSize, viewport: TourSize): number | null {
  if (viewport.width < TOUR_NARROW_WIDTH) {
    return null;
  }
  const { placement } = placeTourCard(hole, card, viewport);
  if (placement !== 'over' && holeIsVisible(hole, viewport)) {
    return 0;
  }
  const pairHeight = hole.height + TOUR_CARD_GAP + card.height;
  if (pairHeight + TOUR_EDGE * 2 <= viewport.height) {
    const wantedTop = Math.round((viewport.height - pairHeight) / 2);
    return hole.top - wantedTop;
  }
  const wantedTop = TOUR_EDGE + card.height + TOUR_CARD_GAP;
  return hole.top - wantedTop;
}
